import { useQuery } from '@tanstack/react-query';
import {
  ScrollArea,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@tutero/ui';

import { accountQuery, type Account } from '@/lib/account';

import { InfoTip } from '@/lib/InfoTip';
import { Loading } from '../Loading';
import { AccountEmailSection } from './AccountEmailSection';
import { AccountNameSection } from './AccountNameSection';
import { AccountPasswordSection } from './AccountPasswordSection';
import { EmailVerificationNotice } from './EmailVerificationNotice';
import { SecondFactorSection } from './SecondFactorSection';
import { reasonFor } from './useAccountWrite';

/**
 * The signed-in staff member's own account (`settings-is-a-dialog` D1): one sheet off the
 * account menu, its sections in a single scrolling column under a header that stays put.
 *
 * Not a tool and not a route: every staff member has an account, so no grant tells one
 * from another, and `the-nav-lists-every-tool` already ruled that a row of this menu has
 * no manifest, no permission and no place in the nav. Nothing here is gated for the same
 * reason the Worker needs no gate: every route it calls reads its subject off the session
 * (D16).
 */
export function SettingsSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-[44.8rem]">
        <SheetHeader className="shrink-0 p-6 pb-4">
          {/* What Settings holds is the sheet's description for a screen reader, and an ⓘ on screen. */}
          <div className="flex items-center gap-2">
            <SheetTitle className="text-headline-5">Settings</SheetTitle>
            <InfoTip label="About Settings" side="right">
              Your own account: your photo, your name, your email address, your
              password and 2FA.
            </InfoTip>
          </div>
          <SheetDescription className="sr-only">
            Your own account: your photo, your name, your email address, your
            password and 2FA.
          </SheetDescription>
        </SheetHeader>
        {open ? <Body /> : null}
      </SheetContent>
    </Sheet>
  );
}

/**
 * The account, read from the Worker each time the sheet opens (D2). Mounted only while
 * the sheet is, so a closed sheet asks for nothing and a reopened one asks again.
 */
function Body() {
  const account = useQuery(accountQuery);

  if (account.isPending) return <Loading label="Reading your account" />;
  if (account.isError) {
    return (
      <p role="alert" className="text-destructive text-regular-m px-6">
        {reasonFor(account.error)}
      </p>
    );
  }
  return <Sections account={account.data} />;
}

function Sections({ account }: { account: Account }) {
  return (
    /* `type="auto"` so a Tab onto a control below the fold brings its row into view. */
    <ScrollArea type="auto" className="min-h-0 flex-1">
      <div className="flex flex-col gap-5 px-6 pb-6">
        {account.emailVerified ? null : (
          <EmailVerificationNotice email={account.email} />
        )}
        <AccountNameSection account={account} />
        <Separator />
        <AccountEmailSection account={account} />
        <Separator />
        <AccountPasswordSection />
        <Separator />
        <SecondFactorSection account={account} />
      </div>
    </ScrollArea>
  );
}
